import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, ClickAwayListener, Divider, ListSubheader, Menu, MenuItem, Popper, Tooltip } from '@mui/material';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import ClearFilterIcon from '@mui/icons-material/FilterAltOffOutlined';
import BookmarkIcon from '@mui/icons-material/BookmarkBorderRounded';
import CommentIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import ExpandIcon from '@mui/icons-material/ExpandMoreRounded';
import { getSavedClipFilters } from '../../api/clipFiltersApi';
import { parseColorToHex } from '../../helpers';
import { useClipFilter } from './context';
import { SavedFilters } from './SavedFilters';
import { FilterOperator, FilterToken, SavedClipFilter, filterFields, filterOperators, matchesText, tokenKey, tokenLabel } from './types';
import buttonStyles from '../modules/Buttons/Buttons.module.scss';
import styles from './Filter.module.scss';

const searchableFields = ['row', 'qualifier', 'category', 'notes', 'comments'] as const;
const fieldIcons = {
  row: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 3v18M15 3v18M5 8h4M5 16h4M15 8h4M15 16h4" /></>,
  qualifier: <path d="M6 3h12v18l-6-4-6 4V3Z" />,
  category: <><path d="M8 7h12v14l-6-4-6 4V7Z" /><path d="M4 17V3h12" /></>,
  notes: <><path d="m16 3 5 5L8 21l-5 1 1-5L16 3Z" /><path d="m14 5 5 5" /></>,
  comments: <path d="M4 4h16v13H9l-5 4V4Z" />,
};
const fieldColors = { row: '#25cd9c', qualifier: '#6767f5', category: '#1cbbcf', notes: '#a647ed', comments: '#f6b900' };
const tokenColor = (color?: string) => color ? (color.startsWith('#') ? color : parseColorToHex(color)) : undefined;

const MatchModes = ({ value, onChange }: { value: FilterOperator; onChange: (value: FilterOperator) => void }) => (
  <div className={styles.matchModes} role="group" aria-label="Text match mode">
    {filterOperators.map((operator) => <button key={operator.value} type="button" aria-pressed={value === operator.value}
      onClick={() => onChange(operator.value)}>{operator.label}</button>)}
  </div>
);

const CommentStatus = () => {
  const { commentsLoading, commentsError, reloadComments } = useClipFilter();
  if (commentsLoading) return <p className={styles.status} role="status"><CircularProgress size={14} /> Loading comments…</p>;
  if (commentsError) return <div className={styles.status} role="alert">Could not load comments.
    <button type="button" onClick={reloadComments}>Retry</button>
  </div>;
  return null;
};

const Commenters = () => {
  const { addToken, removeToken, config, commenters, commentCount, commentsReady } = useClipFilter();
  const [name, setName] = useState('');
  const selectedCommenters = config.tokens.filter((token) => token.field === 'commenter');
  const people = commenters.filter((person) => matchesText([person.username], 'contains', name));
  return <section className={styles.commenters} aria-label="Commented by">
    <h3>Commented by</h3>
    <CommentStatus />
    <button type="button" className={styles.result} disabled={!commentsReady}
      aria-pressed={selectedCommenters.some((token) => token.value === '*')}
      onClick={() => addToken({ field: 'commenter', operator: 'is', value: '*', label: 'Any commenter' })}>
      <CommentIcon /><span>Any commenter</span><small>{commentCount}</small>
    </button>
    <input aria-label="Find a commenter" className={styles.input} value={name} placeholder="Find a commenter…" onChange={(event) => setName(event.target.value)} />
    <div className={styles.people}>
      {people.map((person) => <button type="button" className={styles.result} key={person.id}
        aria-pressed={selectedCommenters.some((token) => token.value === person.id)}
        onClick={() => {
          const selected = selectedCommenters.find((token) => token.value === person.id);
          if (selected) removeToken(selected);
          else addToken({ field: 'commenter', operator: 'is', value: person.id, label: person.username });
        }}>
        <span className={styles.avatar}>{person.avatar_url ? <img src={person.avatar_url} alt="" /> : person.username.slice(0, 1).toUpperCase()}</span>
        <span>{person.username}</span><small title="Comments, including replies">{person.count}</small>
      </button>)}
      {commentsReady && !people.length && <p className={styles.hint}>{name ? 'No matching commenters.' : 'No named commenters yet.'}</p>}
    </div>
  </section>;
};

type MenuOption = { token: FilterToken; title: string; count?: number; blanket?: boolean; field: typeof searchableFields[number] };
const Builder = () => {
  const { values, config, addToken, removeToken, searchFields, setSearchFields, sidebarVisible } = useClipFilter();
  const [query, setQuery] = useState('');
  const [operator, setOperator] = useState<FilterOperator>('contains');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const activeOption = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const checkedFields = searchableFields.filter((field) => searchFields[field]);
  const searchAllToken: FilterToken = { field: 'text', fields: checkedFields, operator, value: query };
  const searchAllAdded = config.tokens.some((token) => tokenKey(token) === tokenKey(searchAllToken));
  const searchAllDisabled = !query.trim() || !checkedFields.length;
  const applyTextFilter = () => {
    if (searchAllDisabled) return;
    addToken(searchAllToken);
    setQuery('');
    setDropdownOpen(false);
  };
  const options = useMemo(() => {
    const items: MenuOption[] = [];
    searchableFields.forEach((field) => {
      if (!searchFields[field]) return;
      const matching = field === 'notes' || field === 'comments' ? undefined
        : values[field].filter((item) => item.count > 0 && (!query.trim() || matchesText([item.value], operator, query)));
      if (matching && !matching.length) return;
      if (query.trim()) items.push({ field, blanket: true, count: matching?.length,
        title: `${filterFields[field]} ${filterOperators.find((mode) => mode.value === operator)?.label.toLowerCase()} “${query.trim()}”`,
        token: { field, operator, value: query.trim() },
      });
      matching?.forEach((item) => items.push({ field, title: item.value, count: item.count,
        token: { field, operator: 'is', value: item.value, color: item.color },
      }));
    });
    return items;
  }, [values, query, operator, searchFields]);
  useEffect(() => setHighlight(0), [query, operator, options.length, searchFields]);
  useEffect(() => { if (!sidebarVisible) setDropdownOpen(false); }, [sidebarVisible]);
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' || event.key === 'Tab') {
      setDropdownOpen(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setDropdownOpen(true);
      if (options.length) setHighlight((previous) => dropdownOpen
        ? (previous + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length
        : event.key === 'ArrowDown' ? 0 : options.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      applyTextFilter();
    }
  };
  useEffect(() => {
    const list = listRef.current;
    const option = activeOption.current;
    if (!dropdownOpen || !list || !option) return;
    const listBounds = list.getBoundingClientRect();
    const optionBounds = option.getBoundingClientRect();
    if (optionBounds.top < listBounds.top) list.scrollTop += optionBounds.top - listBounds.top;
    else if (optionBounds.bottom > listBounds.bottom) list.scrollTop += optionBounds.bottom - listBounds.bottom;
  }, [dropdownOpen, highlight, options.length]);
  return <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
    <div>
      <div className={styles.searchBar} ref={searchRef}>
        <input ref={inputRef} value={query} onChange={(event) => {
          setQuery(event.target.value);
          setDropdownOpen(!!event.target.value.trim());
        }} onKeyDown={onKeyDown} placeholder="Search clips…" aria-label="Search clips" role="combobox"
          aria-expanded={dropdownOpen} aria-autocomplete="list"
          aria-controls={dropdownOpen ? 'clip-filter-options' : undefined}
          aria-activedescendant={dropdownOpen && options.length ? `clip-filter-option-${Math.min(highlight, options.length - 1)}` : undefined}
          aria-describedby={dropdownOpen ? 'clip-filter-keyboard-hint' : undefined} />
        {!searchAllDisabled && <Tooltip title="press enter to apply" placement="top" enterDelay={500} disableFocusListener disableInteractive>
          <span className={styles.enterHint}>Enter ↵</span>
        </Tooltip>}
        <button type="button" aria-label={dropdownOpen ? 'Close filter suggestions' : 'Show filter suggestions'}
          aria-expanded={dropdownOpen} aria-controls={dropdownOpen ? 'clip-filter-options' : undefined}
          onMouseDown={(event) => event.preventDefault()} onClick={() => {
            setDropdownOpen((previous) => !previous);
            inputRef.current?.focus();
          }}><ExpandIcon className={dropdownOpen ? styles.expanded : undefined} /></button>
      </div>
      <fieldset className={styles.searchFields}>
        <legend>Search in</legend>
        {searchableFields.map((field) => <label key={field}>
          <input type="checkbox" checked={searchFields[field]} onChange={(event) => {
            const checked = event.target.checked;
            setSearchFields((previous) => ({ ...previous, [field]: checked }));
          }} />
          {filterFields[field]}
        </label>)}
      </fieldset>
      <MatchModes value={operator} onChange={setOperator} />
      <Tooltip title="Add text filter for selected fields" describeChild>
        <span style={{ display: 'block' }}>
          <button type="button" className={`${buttonStyles.btn} ${buttonStyles.containedPurple} ${searchAllDisabled ? buttonStyles.disabled : ''} ${styles.searchAll}`}
            disabled={searchAllDisabled} aria-pressed={searchAllAdded}
            onClick={applyTextFilter}>
            <FilterIcon /><span>Apply Filter</span>
            <span className={styles.added} aria-label="Added" aria-hidden={!searchAllAdded}>✓</span>
          </button>
        </span>
      </Tooltip>
      <Popper open={dropdownOpen && sidebarVisible} anchorEl={searchRef.current} placement="left-start"
        popperOptions={{ strategy: 'fixed' }} sx={{ zIndex: 1300 }}
        container={() => document.fullscreenElement || document.body}
        modifiers={[{ name: 'offset', options: { offset: [0, 10] } },
          { name: 'flip', options: { fallbackPlacements: ['bottom-end'] } },
          { name: 'preventOverflow', options: { padding: 8 } }]}>
        <div className={styles.dropdown} data-clip-filter-panel>
          <div ref={listRef} className={styles.results} role="listbox" aria-label="Filter suggestions" id="clip-filter-options">
            {options.map((option, index) => {
              const selected = config.tokens.some((token) => tokenKey(token) === tokenKey(option.token));
              return <button key={`${option.blanket ? 'blanket' : 'item'}:${tokenKey(option.token)}`} type="button" role="option" aria-selected={selected} tabIndex={-1} id={`clip-filter-option-${index}`}
                  ref={index === highlight ? activeOption : undefined}
                  className={`${styles.result} ${option.blanket ? styles.blanket : ''} ${index === highlight ? styles.highlight : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlight(index)} onClick={() => selected ? removeToken(option.token) : addToken(option.token)}>
                  {option.blanket && <span className={styles.fieldIcon} style={{ backgroundColor: fieldColors[option.field] }} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{fieldIcons[option.field]}</svg>
                  </span>}
                  {option.token.color && <i className={styles.swatch} style={{ backgroundColor: tokenColor(option.token.color) }} />}
                  <span>{option.title}</span>{option.count !== undefined && <small title={option.blanket ? 'Matching values' : 'Matching clips'}>
                    {option.count}{option.blanket ? ' items' : ' clips'}
                  </small>}
                  {selected && <span className={styles.added} aria-label="Added">✓</span>}
                </button>;
            })}
            {!options.length && <p className={styles.hint}>{!checkedFields.length ? 'Select a field above to search.'
              : query.trim() ? 'No matching values.' : 'No available values. Enter text to search the selected fields.'}</p>}
          </div>
          <p id="clip-filter-keyboard-hint" className={styles.keyboardHint}>↑ ↓ to navigate · Enter to search checked fields · Esc to close</p>
        </div>
      </Popper>
    </div>
  </ClickAwayListener>;
};

export const FilterPanel = () => {
  const { config, setConfig, removeToken, clear, active, waitingForComments, shown, total, showMatchAllZero } = useClipFilter();
  return <div className={styles.panel} data-clip-filter-panel onKeyDown={(event) => event.stopPropagation()}>
    <Builder />
    <Commenters />
    <section className={styles.activeRules} aria-label="Active filter">
      <div className={styles.heading}><h3>Active rules</h3>{active && <button type="button" onClick={clear}>Clear filters</button>}</div>
      <div className={styles.tokens}>
        {config.tokens.map((token) => <span key={tokenKey(token)} className={styles.token}>
          {token.color && <i className={styles.swatch} style={{ backgroundColor: tokenColor(token.color) }} />}
          <span>{tokenLabel(token)}</span>
          <button type="button" aria-label={`Remove ${tokenLabel(token)}`} onClick={() => removeToken(token)}><CloseIcon /></button>
        </span>)}
        {!active && <p className={styles.hint}>Add a rule above to filter clips.</p>}
      </div>
      <div className={styles.toggles}>
        <div role="group" aria-label="Combine filter rules">
          <button type="button" className={showMatchAllZero ? styles.matchAllZero : undefined} aria-pressed={config.match === 'all'}
            onClick={() => setConfig({ ...config, match: 'all' })}>{showMatchAllZero ? 'Match All (0)' : 'Match All'}</button>
          <button type="button" aria-pressed={config.match === 'any'} onClick={() => setConfig({ ...config, match: 'any' })}>Match Any</button>
        </div>
        <div role="group" aria-label="Include or exclude matches">
          <button type="button" aria-pressed={config.mode === 'in'} onClick={() => setConfig({ ...config, mode: 'in' })}>Filter In</button>
          <button type="button" aria-pressed={config.mode === 'out'} onClick={() => setConfig({ ...config, mode: 'out' })}>Filter Out</button>
        </div>
      </div>
      {waitingForComments ? <p className={styles.hint}>Filtering resumes once comments are loaded.</p>
        : <p className={styles.count} role="status">{shown} of {total} clips shown{active && shown === 0 ? ' — all clips are dimmed' : ''}</p>}
    </section>
    <SavedFilters />
  </div>;
};

export const FilterToolbarButton = ({ className }: { className?: string }) => {
  const { active, shown, waitingForComments, sidebarView, sidebarVisible, openFilter, setConfig, clear } = useClipFilter();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [presets, setPresets] = useState<SavedClipFilter[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const closeMenu = () => setPosition(null);
  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const bounds = event.currentTarget.getBoundingClientRect();
    setPresets(null);
    setLoadError(false);
    setPosition({ top: bounds.top, left: bounds.left });
  };

  useEffect(() => {
    if (!position) return;
    let cancelled = false;
    getSavedClipFilters().then(({ data }) => {
      if (!cancelled) setPresets(data.slice().sort((a, b) => a.name.localeCompare(b.name) || Number(a.id) - Number(b.id)));
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });
    return () => { cancelled = true; };
  }, [position]);

  return <>
    <Tooltip title={active ? 'Clip filters are active' : 'Filter clips'}>
      <button type="button" className={`${styles.toolbarButton} ${className || ''}`} data-preserve-clip-workspace
        data-filter-active={active}
        aria-label="Filter clips" aria-pressed={sidebarView === 'filter' && sidebarVisible}
        aria-haspopup="menu" aria-expanded={!!position} onClick={(event) => {
          if (sidebarView !== 'filter' || !sidebarVisible) {
            openFilter();
            return;
          }
          openMenu(event);
        }} onContextMenu={openMenu}>
        <span className={styles.toolbarIcon}><FilterIcon />{active && <small>{waitingForComments ? '…' : shown}</small>}</span>
        <span>Filter</span>
      </button>
    </Tooltip>
    {position && <Menu open onClose={closeMenu} anchorReference="anchorPosition" anchorPosition={position}
      data-preserve-clip-workspace
      PaperProps={{ sx: {
        width: 'max-content', minWidth: 180, maxWidth: 'min(280px, calc(100vw - 32px))',
        maxHeight: '50vh', overflowY: 'auto', '--ui-border-radius': '6px',
      } }}
      MenuListProps={{ 'aria-label': 'Saved filters', sx: {
        p: '4px', gap: 0, alignItems: 'stretch',
        '&& .MuiMenuItem-root': {
          display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1,
          width: '100%', minHeight: 30, m: 0, px: 1, py: 0.5,
          fontSize: 12, lineHeight: '18px', textAlign: 'left', whiteSpace: 'normal', overflowWrap: 'anywhere',
          '& > svg': { width: 16, height: 16, flexShrink: 0, color: 'text.secondary' },
          '& > span': { minWidth: 0 },
        },
      } }}
      onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}>
      <ListSubheader disableSticky sx={{ m: 0, px: 1, py: 0.5, fontSize: 10, lineHeight: '16px', textAlign: 'left' }}>Saved filters</ListSubheader>
      {loadError ? <MenuItem disabled>Could not load saved filters. Reopen to retry.</MenuItem>
        : presets === null ? <MenuItem disabled><CircularProgress size={14} /> Loading saved filters…</MenuItem>
          : presets.length ? presets.map((preset) => <MenuItem key={preset.id} onClick={() => {
            setConfig({ ...preset.config, tokens: preset.config.tokens.map((token) => ({ ...token })) });
            closeMenu();
          }}><BookmarkIcon /><span>{preset.name}</span></MenuItem>)
            : <MenuItem disabled>No saved filters yet.</MenuItem>}
      <Divider component="li" sx={{ my: 0.5 }} />
      <MenuItem disabled={!active} onClick={() => { clear(); closeMenu(); }}><ClearFilterIcon /><span>Clear Filter</span></MenuItem>
    </Menu>}
  </>;
};

export const FilterSummary = () => {
  const { active, shown, total, clear, waitingForComments, commentsError } = useClipFilter();
  if (!active) return null;
  return <div className={styles.toolbarSummary} data-preserve-clip-workspace>
    <FilterIcon /><span role="status">{waitingForComments ? commentsError ? 'Could not load filter comments' : 'Loading filter comments…' : `${shown} of ${total} clips shown`}</span>
    <button type="button" onClick={clear}>Clear filters</button>
  </div>;
};
